import { dia, ui, highlighters, linkTools, elementTools } from '@clientio/jointjs'
import { getNodeHaloHandles } from './halo-handles'
import { HighlighterFrameList } from './selection-frame-list'
import { NodeLink } from '../links'
import { PaperEvents } from '../services/listeners'

export const GridSize = 20
export const ZoomConfig = { min: 0.2, max: 3 }
const getInitialViewportHeight = () => (typeof window !== 'undefined' ? Math.max(window.innerHeight, 1200) : 1200)

/**
 * Enum for Halo Handles actions.
 * @readonly
 * @enum {string}
 */
export const HaloHandlesEnum = {
  LINK: 'link',
  REMOVE_LINKS: 'removeLinks',
  CLONE: 'cloneNode',
  FORK: 'fork',
  SEARCH: 'search'
}

/**
 * Get the configuration options for the Paper.
 * @param {dia.Graph} graph - The graph model to be used by the Paper.
 * @returns {dia.Paper.Options} The configuration options for the Paper.
 */
export const getPaperOptions = graph => ({
  model: graph,
  drawGrid: {
    name: 'fixedDot',
    args: [{ color: 'var(--canvas-dot-color)', thickness: 1 }]
  },
  autoFreeze: true,
  gridSize: GridSize,
  background: { color: 'var(--canvas-bg-color)' },
  width: window.innerWidth - 150,
  height: getInitialViewportHeight(),
  async: true,
  sorting: dia.Paper.sorting.APPROX,
  clickThreshold: 2,
  guard: evt => {
    // cancel the default behavior of guarding the wheel event
    // this allows for the ctrl+wheel zoom when hovering over a CellView
    if (evt.type === 'wheel') {
      evt.data = { guarded: false }
    }
    return false
  },
  markAvailable: true,
  linkPinning: false,
  magnetThreshold: 10,
  snapLinks: { radius: 10 },
  defaultLink: cellView => NodeLink.createFromMagnet(cellView.model.id),
  defaultConnector: {
    name: 'straight',
    args: { cornerType: 'cubic', cornerRadius: 20 }
  },
  defaultConnectionPoint: { name: 'boundary', args: { offset: 5 } },
  defaultRouter: {
    name: 'metro',
    args: {
      padding: 10,
      step: GridSize,
      startDirections: ['right'],
      endDirections: ['left']
    }
  },
  preventContextMenu: true,
  highlighting: { default: false }, // disable error with missing path for highlighting node
  validateMagnet: function (cellView, magnet) {
    return magnet.getAttribute('magnet') !== 'passive'
  },
  validateConnection: function (cellViewS, magnetS, cellViewT, magnetT) {
    // Prevent linking from input ports
    if (magnetS && magnetS.getAttribute('joint-selector') === 'inport') {
      return false
    }
    // Prevent linking from output ports to input ports within one element
    if (cellViewS === cellViewT) {
      return false
    }
    // Prevent linking to output ports
    return (
      magnetT && magnetT.getAttribute('joint-selector') === 'inport' && magnetT.getAttribute('magnet') === 'passive'
    )
  }
})

/**
 * Get the configuration options for the PaperScroller.
 * @param {dia.Paper} paper - The Paper instance to be used by the PaperScroller.
 * @returns {joint.ui.PaperScroller.Options} The configuration options for the PaperScroller.
 */
export const getScrollerOptions = paper => ({
  paper: paper,
  autoResizePaper: true,
  baseWidth: 400,
  baseHeight: getInitialViewportHeight(),
  cursor: 'grab',
  borderless: true,
  scrollWhileDragging: true,
  // Responsive padding based on viewport size
  padding: scroller => {
    const { width, height } = scroller.getClientSize()
    return {
      left: width / 8,
      right: width / 8,
      top: height / 2,
      bottom: height / 2
    }
  }
})

/**
 * Get the configuration options for the Halo - quick node options (next connections, remove, link etc.).
 * @param {dia.ElementView} elementView - The ElementView instance to be used by the Halo.
 * @param {Array} [filteredNodes] - Optional filtered nodes from search.
 * @returns {joint.ui.Halo} The configuration options for the Halo.
 */
export const getHaloPanel = (
  elementView,
  filteredNodes = null,
  isTempNode = false,
  searchTerm = '',
  autoFocus = false
) => {
  const halo = new ui.Halo({
    cellView: elementView,
    type: 'overlay',
    boxContent: false,
    clearAll: true,
    groups: {
      'halo-group': {
        top: '0',
        left: '100%',
        verticalAlign: 'top',
        horizontalAlign: 'left',
        trackDirection: 'column',
        trackCount: 1,
        gap: '4px'
      }
    },
    theme: 'material',
    magnet: (magnetElementView, end) => {
      // automatically set link connection target to input port and  source to output port
      const portName = end === 'target' ? 'inport' : 'outport'
      return magnetElementView.el.querySelector(`circle[joint-selector=${portName}]`) || magnetElementView.el
    }
  })

  halo.removeHandles() // remove all default halo handles

  // add custom handles depending on node type with optional filtered nodes
  getNodeHaloHandles(elementView.model, filteredNodes, isTempNode, searchTerm).forEach(handle => halo.addHandle(handle))

  // Set up search input event listeners after halo is rendered
  setTimeout(() => {
    const searchInput = halo.el.querySelector(`#halo-search-${elementView.model.id}`)
    const searchContainer = halo.el.querySelector('.halo-search-container')

    if (searchInput && searchContainer) {
      // Prevent halo handle from interfering with input interaction
      searchContainer.addEventListener('mousedown', e => {
        e.stopPropagation()
      })

      searchContainer.addEventListener('click', e => {
        e.stopPropagation()
        searchInput.focus()
      })

      searchInput.addEventListener('input', e => {
        elementView.paper.trigger(PaperEvents.HaloSearchInput, e.target.value, elementView.model.id)
      })

      searchInput.addEventListener('focus', _e => {
        elementView.paper.trigger(PaperEvents.HaloSearchFocus, elementView.model.id)
      })

      // Only trigger blur if the input is actually losing focus to something outside the halo
      searchInput.addEventListener('blur', _e => {
        // Add a small delay to check if focus is moving to a new search input in the recreated halo
        setTimeout(() => {
          const activeElement = document.activeElement
          const isNewSearchInput = activeElement && activeElement.id === `halo-search-${elementView.model.id}`

          // Only trigger blur if focus is not on a new search input with the same ID
          if (!isNewSearchInput) {
            elementView.paper.trigger(PaperEvents.HaloSearchBlur, elementView.model.id)
          }
        }, 10)
      })

      searchInput.addEventListener('mousedown', e => {
        e.stopPropagation()
      })

      searchInput.addEventListener('click', e => {
        e.stopPropagation()
      })

      // Prevent keyboard events from bubbling up to the paper
      searchInput.addEventListener('keydown', e => {
        e.stopPropagation()
      })

      searchInput.addEventListener('keyup', e => {
        e.stopPropagation()
      })

      if (autoFocus) {
        searchInput.focus()
        // Set cursor position to end of text
        searchInput.setSelectionRange(searchTerm.length, searchTerm.length)
      }
    }
  }, 0)

  return halo
}

/**
 * Get the configuration options for the selection.
 * @param {dia.Paper} paper - The Paper instance to be used by the selection.
 * @returns {joint.ui.Selection.Options} The configuration options for the selection.
 */
export const getSelectionOptions = paper => ({
  paper: paper,
  handles: [],
  useModelGeometry: false,
  strictSelection: true,
  allowCellInteraction: true,
  boxContent: false,
  frames: new HighlighterFrameList({
    highlighter: highlighters.addClass,
    selector: 'root',
    options: { className: 'highlighted' }
  }),
  wrapper: {
    visibility: selection => selection.collection.filter(n => n.isElement()).length > 1
  }
})

/**
 * Configuration options for tooltips.
 * @return {joint.ui.Tooltip.Options}
 */
export const getTooltipConfig = () => ({
  theme: 'default',
  rootTarget: document.body,
  target: '[data-tooltip]',
  direction: undefined,
  position: ui.Tooltip.TooltipPosition.Top,
  animation: true,
  padding: 15
})

/**
 * Get the configuration options for the link tools.
 * @returns {joint.dia.ToolsView} The configuration options for the link tools.
 */
export const getLinkToolConfig = () =>
  new dia.ToolsView({
    name: 'link-tools',
    tools: [
      new linkTools.Remove({
        action: (evt, view) => {
          view.paper.trigger(PaperEvents.LinkRemoveBtnClick, view)
        },
        distance: -40,
        offset: -15,
        scale: 1.5
      }),
      new linkTools.TargetArrowhead({
        scale: 1.5,
        fill: 'var(--diagram-link-color)'
      }) // makes link target head draggable
    ]
  })

export const getNodeToolConfig = (x = 210) =>
  new dia.ToolsView({
    name: 'node-tools',
    tools: [
      new elementTools.Remove({
        action: (evt, view) => {
          view.paper.trigger(PaperEvents.NodeRemoveBtnClick, view)
        },
        x,
        y: -10,
        scale: 1.3
      })
    ]
  })

/**
 * Creates a confirmation dialog for removing element (node/link), with customizable content.
 * The dialog provides two options: "Yes" and "No". Based on the user's choice,
 * a callback function is executed, and the dialog is closed.
 *
 * @param {Function} removeLinkCb - The callback function to execute when a button is clicked.
 * @param {string} [content] - The content to display in the dialog. Defaults to a predefined message.
 */
export const createRemoveDialog = (removeLinkCb, content = 'Should the condition list be updated for CASE node?') => {
  const buttons = [
    { action: 'yes', content: 'Yes' },
    { action: 'no', content: 'No' }
  ]
  const dialog = new ui.Dialog({
    width: 400,
    type: 'warning',
    title: ' ',
    content: `<b>${content}</b>`,
    closeButton: false,
    buttons
  })

  dialog.el.style.zIndex = 2000

  buttons.forEach(btn =>
    dialog.on(`action:${btn.action}`, () => {
      removeLinkCb(btn.action === 'yes')
      dialog.close()
    })
  )

  dialog.open()
}

/**
 * Displays a user message as a flash alert on the UI.
 * The message is styled as an alert and automatically disappears after 5 seconds.
 *
 * @param {string} content - The content of the message to be displayed.
 */
export const createUserMessage = content => {
  new ui.FlashMessage({
    type: 'alert',
    content: `<em>${content}</em>`,
    closeAnimation: { delay: 7500 }
  }).open()
}
